import {Component} from '@angular/core';
import {IBaseModalComponent} from '../../../../../sdk/src/components/ibase-modal/ibase-modal.component';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {DynamicDialogConfig} from 'primeng/dynamicdialog';
import {TreeNode} from 'primeng/api';
import {CpmCTaskApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-task.api-model';
import {FormControl} from '@angular/forms';
import {CpmCTaskDependencyApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-task-dependency.api-model';
import {CpmCTaskDeleteApiSchema} from '../../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-task.api-schemas';
import {ReenforcedConfirmModalComponent} from '../../../../../sdk/src/modals/reenforced-confirm-modal/reenforced-confirm-modal.component';
import {TaskUtils} from '../../../utils/task.utils';
import {TaskTreeViewExtension} from '../../../api-extensions/views/task-tree.view-extension';
import {TreeUtils} from '../../../../../sdk/src/utils/tree.utils';

export class TaskDependsWarningModalConfig {
    constructor(
        public itemToDelete: CpmCTaskApiModel,
        public items: TaskTreeViewExtension[],
        public deleteItemService: (item: CpmCTaskApiModel, payload?: CpmCTaskDeleteApiSchema) => Promise<boolean>
    ) {}
}

@Component({
    selector: 'app-task-depends-confirm-warning-modal',
    templateUrl: './task-depends-confirm-warning-modal.html',
    imports: [PrimeNgImportsModule]
})
export class TaskDependsWarningModalComponent extends IBaseModalComponent<
    TaskDependsWarningModalComponent,
    TaskDependsWarningModalConfig
> {
    protected options: TreeNode<TaskTreeViewExtension>[] = [];
    protected newDependencyControls = new Map<string, FormControl<TreeNode<TaskTreeViewExtension> | null>>();
    protected newDependentsControls = new Map<string, FormControl<TreeNode<TaskTreeViewExtension> | null>>();

    // ===============================================================
    // ===============================================================

    constructor() {
        super();
    }

    // ===============================================================
    // ===============================================================

    public get dependencies(): CpmCTaskDependencyApiModel[] {
        if (!this.config.data?.itemToDelete.dependency_relationships) return [];
        return this.config.data?.itemToDelete.dependency_relationships;
    }

    public get dependents(): CpmCTaskDependencyApiModel[] {
        if (!this.config.data?.itemToDelete.dependent_relationships) return [];
        return this.config.data?.itemToDelete.dependent_relationships;
    }

    /**
     * Get dialog service parameters
     * @param config modal configuration
     * @returns DynamicDialogConfig<TaskDependsWarningModalConfig>
     */
    public static getDialogServiceParameters(
        config: TaskDependsWarningModalConfig
    ): DynamicDialogConfig<TaskDependsWarningModalConfig> {
        return {
            modal: true,
            header: 'Warning',
            data: config,
            maximizable: true,
            draggable: true,
            resizable: true,
            contentStyle: {
                'max-height': '80vh'
            }
        };
    }

    // ===============================================================
    // ===============================================================

    protected override async onReady(): Promise<void> {
        if (this.config.data) {
            // Build tree without filtering to avoid orphan nodes
            this.options = TaskUtils.buildTaskTree(this.config.data.items);
            TaskUtils.sortTaskTree(this.options);
            // Mark the item to delete as non-selectable
            TreeUtils.disableCurrentTreeNode(this.options, this.config.data.itemToDelete.uuid);

            this.config.data.itemToDelete.dependent_relationships?.forEach((item) => {
                this.newDependentsControls.set(
                    item.task_uuid,
                    new FormControl<TreeNode<TaskTreeViewExtension> | null>(null)
                );
            });
            this.config.data.itemToDelete.dependency_relationships?.forEach((item) => {
                this.newDependencyControls.set(
                    item.depends_on_task_uuid,
                    new FormControl<TreeNode<TaskTreeViewExtension> | null>(null)
                );
            });
        }
    }

    protected override async onDestroy(): Promise<void> {
        // nothing to init
    }

    // ===============================================================
    // ===============================================================

    protected onCancel() {
        this.ref.close(null);
    }

    /**
     * Call delete callback and close modal based on result
     */
    protected async onConfirm() {
        const dependencies = new Map<string, string>();
        this.newDependencyControls.forEach((formControl, taskUuid) => {
            if (formControl.value?.key) {
                dependencies.set(taskUuid, formControl.value.key);
            }
        });
        const dependents = new Map<string, string>();
        this.newDependentsControls.forEach((formControl, taskUuid) => {
            if (formControl.value?.key) {
                dependents.set(taskUuid, formControl.value.key);
            }
        });

        // Show loading indicator
        this.showLoading = true;

        // Call the delete service with the payload
        this.globalsService.dialogService
            .open(
                ReenforcedConfirmModalComponent,
                ReenforcedConfirmModalComponent.getDialogServiceParameters({
                    isCreate: false,
                    message: `Are you sure that you want to delete task '${this.config.data!.itemToDelete.name}'?`,
                    confirmation: this.config.data!.itemToDelete.name
                })
            )
            .onClose.subscribe(async (res: boolean) => {
                if (res) {
                    const succed = await this.config.data?.deleteItemService(this.config.data!.itemToDelete, {
                        dependencies_mapping: Object.fromEntries(dependencies),
                        dependent_mapping: Object.fromEntries(dependents)
                    });
                    if (succed) {
                        this.ref.close(true);
                    }
                }
                this.showLoading = false;
            });
    }
}
